// src/services/auth.ts

import apiClient from './api';
import { Prisma, ProductStatus } from '@prisma/client';
import { AxiosError, AxiosResponse } from 'axios';
import { Order } from '@prisma/client';
type Category = {
  id: number;
  name: string;
  parentId: number | null;
  parent?: Category | null;
  children: Category[];
};
type User = Prisma.UserGetPayload<{
  include: {
    orders: {include:{
      shippingAddress: true,
      items: true,
      payment: true,
      billingAddress: true;
      
    }},
    addresses: true;
  };
}>;
type Product = Prisma.ProductGetPayload<{
  include: {
    images: true,
    attributes: {
      include:{
        values:true;
      }
    }
    variants: {
      include: {
        images: true,
        attributes: {
          include:{
            values:true;
          }
        }
      };
    }
  };
}>;
const LOGIN_URL = '/auth/login';
const LOGOUT_URL = '/auth/logout';
const ME_URL = '/auth/me';
const REGISTER_URL = '/auth/register';
const PASSWORD_RESET_URL = '/auth/request-password-reset';



const CREATE_USER_URL = '/admin/users/create';
const CREATE_ORDER_URL = '/orders/create';
const CREATE_PRODUCT_URL = '/admin/products/create';
const CREATE_CATEGORY_URL = '/admin/categories/create';

const GET_ORDERS_URL = '/admin/orders'
const GET_PRODUCTS_URL = '/products'
const GET_USERS_URL = '/admin/users'
const GET_CATEGORIES_URL = '/categories';


interface LoginResponse {
  user: User;
  message: string;
}
interface RegisterResponse {
  message: string;
  user: User;
}
interface MeResponse {
  user: User;
}


interface CreateUserResponse{
  message: string;
}
interface GetUserResponse {
  user: User
}
interface GetUsersResponse {
  users: User[]
}
interface UpdateUserResponse{
  message: string;
}
interface DeleteUserResponse{
  message: string;
}


interface CreateProductResponse{
  message: string;
}
interface GetProductsResponse {
  products: Product[]
}

interface UpdateProductResponse{
  message: string;
}
interface DeleteProductResponse{
  message: string;
}


interface CreateOrderResponse{
  message: string;
}
interface GetOrdersResponse {
  data: {orders: Order[]};
  meta: { 
    totalOrders: number;
    page: number;
    limit: number;
  }
}
interface GetOrderResponse {
  order: Order;
}
interface UpdateOrderResponse{
  message: string;
}
interface DeleteOrderResponse{
  message: string;
}


interface CreateCategoryResponse {
  message: string;
}
interface GetCategoriesResponse {
  categories: Category[];
}
interface UpdateCategoryResponse{
  message: string;
}
interface DeleteCategoryResponse{
  message: string;
}


export const createUser = async (
  user: Partial<User>,
): Promise<CreateUserResponse> => {
  try {
    const response = await apiClient.post<CreateUserResponse>(
      CREATE_USER_URL,
      { user },
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      console.error('Axios Error:', {
        status: error.response?.status,
        data: error.response?.data,
      });
      const errorMessage =
        error.response?.data?.message || 'Failed to create user.';
      throw new Error(errorMessage);
    }

    if (error instanceof Error) {
      console.error('Error:', error.message);
      throw new Error(`User creation failed: ${error.message}`);
    }
    throw new Error('User creation failed due to an unknown error.');
  }
};
export const getUser = async (
  id: number,
): Promise<GetUserResponse> => {
  try {
    const response = await apiClient.get<GetUserResponse>(
      `/admin/user/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to get user';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to get user due to an unknown error.'
      );
    }
  }
};
export const getUsers = async (
  page: number = 1,
  limit: number = 10,
  searchTerm: string = '',
  sortBy: string = 'date',
): Promise<GetUsersResponse> => {
  try {
    // Construct query parameters
    const params = {
      page,
      limit,
      search: searchTerm || undefined,
      sort: sortBy || undefined,
    };

    // Perform the GET request
    const response: AxiosResponse<GetUsersResponse> = await apiClient.get(
      GET_USERS_URL,
      {
        params,
      }
    );
    return response.data;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch users due to an unknown error.';
    console.error('Error fetching users:', errorMessage);
    throw new Error(errorMessage);
  }
};
export const updateUser = async (
  id: number,
  user: Partial<User>,
): Promise<UpdateUserResponse> => {
  try {
    const response = await apiClient.put<UpdateUserResponse>(
      `/admin/user/${id}`,
      user,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update the user.';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to update the user due to an unknown error.');
    }
  }
};
export const deleteUser = async (
  id: number,
): Promise<DeleteUserResponse> => {
  try {
    const response = await apiClient.delete<DeleteUserResponse>(
      `/admin/user/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to delete user';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to delete user due to an unknown error.'
      );
    }
  }
};


export const createProduct = async (
  product: Partial<Product>,
): Promise<CreateProductResponse> => {
  try {
    const response = await apiClient.post<CreateProductResponse>(
      CREATE_PRODUCT_URL,
      product,
      {
        headers: {
          'Content-Type': 'application/json', // Explicitly set the content type
        },
      }
    );

    // Return the full response data
    return {
      message: response.data.message || 'Product created successfully.',
    };
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      // Log Axios-specific error details for debugging
      console.error('Axios Error:', {
        status: error.response?.status,
        data: error.response?.data,
      });

      const errorMessage =
        error.response?.data?.message || 'Failed to create the product.';
      throw new Error(errorMessage);
    }

    if (error instanceof Error) {
      // Handle non-Axios generic errors
      console.error('Error:', error.message);
      throw new Error(`Product creation failed: ${error.message}`);
    }

    // Handle unknown errors
    throw new Error('An unknown error occurred while creating the product.');
  }
};
export const getProduct = async (
  id: string,
) => {
  try {
    const response = await apiClient.get(
      `/products/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to get product';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to get product due to an unknown error.'
      );
    }
  }
};
export const getProducts = async (
  page: number = 1,
  limit: number = 10,
  searchTerm: string = '',
  selectedCategory: string = '',
  sortBy: string = 'price',
  priceRange: [number, number] = [0, 10000],
): Promise<GetProductsResponse> => {
  try {
    // Construct query parameters
    const params = {
      page,
      limit,
      search: searchTerm || undefined,
      category: selectedCategory || undefined,
      sort: sortBy || undefined,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    };

    // Perform the GET request
    const response: AxiosResponse<GetProductsResponse> = await apiClient.get(
      GET_PRODUCTS_URL,
      {
        params,
      }
    );
    return response.data;
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch products due to an unknown error.';
    console.error('Error fetching products:', errorMessage);
    throw new Error(errorMessage);
  }
};
export const updateProduct = async (
  id: string,
  product: Partial<Product>,
): Promise<UpdateProductResponse> => {
  try {
    const response = await apiClient.put<UpdateProductResponse>(
      `/admin/products/${id}`,
      product,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update the product.';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to update the product due to an unknown error.');
    }
  }
};
export const deleteProduct = async (
  id: string,
): Promise<DeleteProductResponse> => {
  try {
    const response = await apiClient.delete<DeleteProductResponse>(
      `/admin/products/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to delete product';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to delete product due to an unknown error.'
      );
    }
  }
};


export const createOrder = async (
  order: Partial<Order>,
): Promise<CreateOrderResponse> => {
  try {
    const response = await apiClient.post<CreateOrderResponse>(
      CREATE_ORDER_URL,
      order,
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Поръчката е неуспешна';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Ordering failed due to an unknown error.'
      );
    }
  }
};
export const getOrder = async (
  id: number,
): Promise<GetOrderResponse> => {
  try {
    const response = await apiClient.get<GetOrderResponse>(
      `/orders/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to get user orders';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to get user orders due to an unknown error.'
      );
    }
  }
};
export const updateOrder = async (
  id: number,
  order: Partial<Order>,
): Promise<UpdateOrderResponse> => {
  try {
    const response = await apiClient.put<UpdateOrderResponse>(
      `/admin/orders/${id}`,
      order,
    );

    // Return the data portion of the response
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      // Extract error message from Axios response if available
      const errorMessage =
        error.response?.data?.message || 'Failed to update the order.';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      // Handle generic JavaScript errors
      throw new Error(error.message);
    } else {
      // Handle unknown errors
      throw new Error('Failed to update the order due to an unknown error.');
    }
  }
};
export const deleteOrder = async (
  id: number,
): Promise<DeleteOrderResponse> => {
  try {
    const response = await apiClient.delete<DeleteOrderResponse>(
      `/admin/orders/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to delete order';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to delete order due to an unknown error.'
      );
    }
  }
};
export const getOrders = async (
  page: number,
  limit: number,
  searchTerm: string,
  statusFilter: string,
  sortBy: string,
): Promise<GetOrdersResponse> => {
  try {
    const response = await apiClient.get<GetOrdersResponse>(
      GET_ORDERS_URL,
      {
        params: {
          page,
          limit,
          searchTerm,
          statusFilter,
          sortBy,
        },
      }
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to get user orders';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to get user orders due to an unknown error.'
      );
    }
  }
};


export const createCategory = async (
  categoryName: string,
  parentCategoryId?: number
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post<CreateCategoryResponse>(
      CREATE_CATEGORY_URL,
      {
        name: categoryName,
        parentId: parentCategoryId || null, // Ensure parentId is null if undefined
      },
    );

    // Return only the message from the response data
    return {
      message: response.data.message || 'Category created successfully.',
    };
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      // Log error details for debugging
      console.error('Axios Error:', {
        status: error.response?.status,
        data: error.response?.data,
      });
      // Extract error message or use fallback
      const errorMessage =
        error.response?.data?.message || 'Creating category failed.';
      throw new Error(errorMessage);
    }

    if (error instanceof Error) {
      // Generic error handling
      console.error('Error:', error.message);
      throw new Error(`Category creation failed: ${error.message}`);
    }

    // Handle unknown errors
    throw new Error('Category creation failed due to an unknown error.');
  }
};
export const getCategories = async (
): Promise<Category[]> => {
  try {
    const response = await apiClient.get<GetCategoriesResponse>(GET_CATEGORIES_URL);

    // Ensure categories exist in the response and default to an empty array if undefined
    return response.data.categories || [];
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      // Log Axios-specific error details for debugging
      console.error('Axios Error:', {
        status: error.response?.status,
        data: error.response?.data,
      });

      const errorMessage =
        error.response?.data?.message || 'Failed to fetch categories.';
      throw new Error(errorMessage);
    }

    if (error instanceof Error) {
      // Log generic error messages
      console.error('Error:', error.message);
      throw new Error(`Fetching categories failed: ${error.message}`);
    }

    // Handle unknown errors
    throw new Error('An unknown error occurred while fetching categories.');
  }
};
export const updateCategory = async (
  id: number,
  categoryName: string,
  parentCategoryId?: number
): Promise<{message: string }> => {
  try {
    const response = await apiClient.put<UpdateCategoryResponse>(
      `/admin/categories/${id}`,
      { categoryName, parentCategoryId},
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Updating category failed';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Updating category failed due to an unknown error.');
    }
  }
};
export const deleteCategory = async (
  id: number,
): Promise<DeleteCategoryResponse> => {
  try {
    const response = await apiClient.delete<DeleteCategoryResponse>(
      `/admin/categories/${id}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Deleting category failed';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Deleting category failed due to an unknown error.'
      );
    }
  }
};


export const updateProductStatus = async (
  productId: string,
  newStatus: ProductStatus,
): Promise<unknown> => {
  try {
    const response = await apiClient.put(
      `/products/${productId}/updateStatus`,
      { status: newStatus },
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Updating product status failed';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Updating product status failed due to an unknown error.'
      );
    }
  }
};
export const sendPasswordResetEmail = async (
  email: string,
): Promise<void> => {
  try {
    await apiClient.post(
      PASSWORD_RESET_URL,
      { email },
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to send password reset email';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'Failed to send password reset email due to an unknown error.'
      );
    }
  }
};
export const isUserCookieAvailable = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/check-cookie');
    const data = await response.json();
    return data.isUserCookieAvailable;
  } catch {
    return false;
  }
};


export const login = async (
  email: string,
  password: string,
): Promise<{ user: User; message: string }> => {
  try {
    const response = await apiClient.post<LoginResponse>(
      LOGIN_URL,
      {
        email,
        password,
      },
    );
    return {
      user: response.data.user,
      message: response.data.message || 'Login successful',
    };
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Login failed';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Login failed due to an unknown error.');
    }
  }
};
export const register = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  promotionalOptIn: boolean,
): Promise<{user: User; message: string }> => {
  try {
    const response = await apiClient.post<RegisterResponse>(
      REGISTER_URL,
      {
        email,
        password,
        firstName,
        lastName,
        promotionalOptIn,
      },
    );
    return {
      user: response.data.user,
      message: response.data.message || 'Register successful',
    };
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Registration failed';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Registration failed due to an unknown error.');
    }
  }
};
export const logout = async (
): Promise<void> => {
  try {
    await apiClient.post(LOGOUT_URL, null);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error('Logout failed');
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Logout failed due to an unknown error.');
    }
  }
};
export const getCurrentUser = async (p0: string): Promise<User> => {
  try {
    const response = await apiClient.get<MeResponse>(ME_URL);
    return response.data.user;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch user';
      throw new Error(errorMessage);
    } else if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error(
        'An unknown error occurred while fetching the current user.'
      );
    }
  }
};